const EDITOR_NODE_ID = 'data-email-editor-node-id'

export const isFullHtmlDocument = (value: string): boolean =>
  /<!doctype\s+html/i.test(value) || /<html(?:\s[^>]*)?>[\s\S]*<\/html>/i.test(value)

export const looksLikeHtmlSource = (value: string): boolean =>
  /<!doctype\s+html|<\/?[a-z][^>]*>/i.test(value.trim())

const annotateBodyElements = (body: HTMLElement): void => {
  body.querySelectorAll('*').forEach((element, index) => {
    element.setAttribute(EDITOR_NODE_ID, String(index))
  })
}

const removeEditorNodeIds = (root: ParentNode): void => {
  root.querySelectorAll(`[${EDITOR_NODE_ID}]`).forEach((element) => {
    element.removeAttribute(EDITOR_NODE_ID)
  })
}

export const prepareHtmlDocumentForEditor = (value: string): string => {
  if (!isFullHtmlDocument(value)) return value

  const document = new DOMParser().parseFromString(value, 'text/html')
  annotateBodyElements(document.body)
  return document.body.innerHTML
}

const getEditorNode = (root: ParentNode, id: string): Element | null =>
  root.querySelector(`[${EDITOR_NODE_ID}="${id}"]`)

const getDirectStructure = (element: Element): string =>
  Array.from(element.childNodes)
    .filter((node) => node.nodeType === Node.ELEMENT_NODE)
    .map((node) => {
      const child = node as Element
      return `${child.tagName}:${child.getAttribute(EDITOR_NODE_ID) ?? 'editor-created'}`
    })
    .join('|')

const getOwnedTextNodes = (element: Element): Text[] => {
  const nodes: Text[] = []
  const walker = element.ownerDocument.createTreeWalker(element, NodeFilter.SHOW_TEXT)

  while (walker.nextNode()) {
    const textNode = walker.currentNode as Text
    const owner = textNode.parentElement?.closest(`[${EDITOR_NODE_ID}]`)

    if (owner === element && textNode.data.trim().length > 0) {
      nodes.push(textNode)
    }
  }

  return nodes
}

const syncChangedAttributes = (
  originalElement: Element,
  baselineElement: Element,
  editedElement: Element
): void => {
  const attributeNames = new Set([
    ...Array.from(baselineElement.attributes, ({name}) => name),
    ...Array.from(editedElement.attributes, ({name}) => name),
  ])

  attributeNames.delete(EDITOR_NODE_ID)

  attributeNames.forEach((name) => {
    const baselineValue = baselineElement.getAttribute(name)
    const editedValue = editedElement.getAttribute(name)

    if (baselineValue === editedValue) return

    if (editedValue === null) {
      originalElement.removeAttribute(name)
    } else {
      originalElement.setAttribute(name, editedValue)
    }
  })
}

const hasChangedMappedDescendant = (
  baselineRoot: ParentNode,
  editedElement: Element
): boolean =>
  Array.from(editedElement.querySelectorAll(`[${EDITOR_NODE_ID}]`)).some((editedChild) => {
    const childId = editedChild.getAttribute(EDITOR_NODE_ID)
    const baselineChild = childId ? getEditorNode(baselineRoot, childId) : null

    return (
      !baselineChild ||
      baselineChild.textContent !== editedChild.textContent ||
      getDirectStructure(baselineChild) !== getDirectStructure(editedChild)
    )
  })

const replaceHtmlDocumentBody = (documentHtml: string, bodyHtml: string): string =>
  documentHtml.replace(
    /(<body(?:\s[^>]*)?>)[\s\S]*?(<\/body>)/i,
    (_match, openingBodyTag, closingBodyTag) =>
      `${openingBodyTag}${bodyHtml}${closingBodyTag}`
  )

export const mergeEditorBodyIntoHtmlDocument = (
  documentHtml: string,
  baselineEditorBody: string,
  editedEditorBody: string
): string => {
  if (!isFullHtmlDocument(documentHtml) || baselineEditorBody === editedEditorBody) {
    return documentHtml
  }

  const originalDocument = new DOMParser().parseFromString(documentHtml, 'text/html')
  const baselineDocument = new DOMParser().parseFromString(baselineEditorBody, 'text/html')
  const editedDocument = new DOMParser().parseFromString(editedEditorBody, 'text/html')

  annotateBodyElements(originalDocument.body)

  const structurallyChangedIds = Array.from(
    editedDocument.body.querySelectorAll(`[${EDITOR_NODE_ID}]`)
  )
    .filter((editedElement) => {
      const id = editedElement.getAttribute(EDITOR_NODE_ID)
      const baselineElement = id ? getEditorNode(baselineDocument.body, id) : null
      return baselineElement && getDirectStructure(baselineElement) !== getDirectStructure(editedElement)
    })
    .filter((editedElement) => {
      const parentChanged = editedElement.parentElement?.closest(`[${EDITOR_NODE_ID}]`)
      if (!parentChanged) return true

      const parentId = parentChanged.getAttribute(EDITOR_NODE_ID)
      const baselineParent = parentId ? getEditorNode(baselineDocument.body, parentId) : null
      return !baselineParent || getDirectStructure(baselineParent) === getDirectStructure(parentChanged)
    })
    .map((element) => element.getAttribute(EDITOR_NODE_ID))
    .filter((id): id is string => Boolean(id))

  structurallyChangedIds.forEach((id) => {
    const originalElement = getEditorNode(originalDocument.body, id)
    const editedElement = getEditorNode(editedDocument.body, id)
    if (!originalElement || !editedElement) return

    const replacement = editedElement.cloneNode(true) as Element
    removeEditorNodeIds(replacement)
    originalElement.innerHTML = replacement.innerHTML
  })

  editedDocument.body.querySelectorAll(`[${EDITOR_NODE_ID}]`).forEach((editedElement) => {
    const id = editedElement.getAttribute(EDITOR_NODE_ID)
    if (!id || structurallyChangedIds.includes(id)) return

    const originalElement = getEditorNode(originalDocument.body, id)
    const baselineElement = getEditorNode(baselineDocument.body, id)
    if (!originalElement || !baselineElement) return

    syncChangedAttributes(originalElement, baselineElement, editedElement)

    const baselineTextNodes = getOwnedTextNodes(baselineElement)
    const editedTextNodes = getOwnedTextNodes(editedElement)
    const originalTextNodes = getOwnedTextNodes(originalElement)

    if (
      baselineTextNodes.length !== editedTextNodes.length ||
      baselineTextNodes.length !== originalTextNodes.length
    ) {
      if (
        baselineElement.textContent !== editedElement.textContent &&
        !hasChangedMappedDescendant(baselineDocument.body, editedElement)
      ) {
        const replacement = editedElement.cloneNode(true) as Element
        removeEditorNodeIds(replacement)
        originalElement.innerHTML = replacement.innerHTML
      }
      return
    }

    const hasOwnedTextChange = baselineTextNodes.some(
      (baselineTextNode, index) => baselineTextNode.data !== editedTextNodes[index].data
    )

    baselineTextNodes.forEach((baselineTextNode, index) => {
      const editedTextNode = editedTextNodes[index]
      if (baselineTextNode.data !== editedTextNode.data) {
        originalTextNodes[index].data = editedTextNode.data
      }
    })

    if (
      !hasOwnedTextChange &&
      baselineElement.textContent !== editedElement.textContent &&
      !hasChangedMappedDescendant(baselineDocument.body, editedElement)
    ) {
      const replacement = editedElement.cloneNode(true) as Element
      removeEditorNodeIds(replacement)
      originalElement.innerHTML = replacement.innerHTML
    }
  })

  removeEditorNodeIds(originalDocument.body)
  return replaceHtmlDocumentBody(documentHtml, originalDocument.body.innerHTML)
}
